import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import LandingPage from "@/pages/LandingPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import DataEntryPage from "@/pages/DataEntryPage";
import ReportsPage from "@/pages/ReportsPage";
import ReportViewerPage from "@/pages/ReportViewerPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import MonthlyReportPage from "@/pages/client/MonthlyReportPage";
import SectorsPage from "@/pages/SectorsPage";
import ImportDataPage from "@/pages/admin/ImportDataPage";
import PendingClientsPage from "@/pages/admin/PendingClientsPage";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import CartographyPage from "@/pages/admin/CartographyPage";
import KPIsPage from "@/pages/admin/KPIsPage";
import CampaignsPage from "@/pages/admin/CampaignsPage";
import ReportPromptsAdminPage from "@/pages/admin/ReportPromptsAdminPage";
import ReportReviewPage from "@/pages/admin/ReportReviewPage";
import VeilleProactiveHealthPage from "@/pages/admin/VeilleProactiveHealthPage";
import MentionsLegalesPage from "@/pages/legal/MentionsLegalesPage";
import CGUPage from "@/pages/legal/CGUPage";
import CGVPage from "@/pages/legal/CGVPage";
import ConfidentialitePage from "@/pages/legal/ConfidentialitePage";

function ClientReportViewer({ user, onLogout }) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png"
              alt="BENCHORY" className="h-9"
            />
            <div className="h-6 w-px bg-gray-200" />
            <button onClick={() => nav('/espace-client')} className="text-sm text-gray-500 hover:text-[#1B2A4A] font-medium">
              Mes rapports
            </button>
          </div>
          <span className="text-sm text-[#1B2A4A] font-medium">{user?.name}</span>
        </div>
      </nav>
      <div className="max-w-5xl mx-auto px-6 py-8">
        <ReportViewerPage isClientView={true} />
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("benchory_user");
    const token = localStorage.getItem("benchory_token");
    if (stored && token) {
      try { setUser(JSON.parse(stored)); } catch { localStorage.clear(); }
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("benchory_token");
    localStorage.removeItem("benchory_user");
    setUser(null);
  };

  if (loading) return null;

  const isAdmin = user?.role === "admin";
  const isClient = user?.role === "client";

  return (
    <BrowserRouter>
      <Routes>
        {/* Public */}
        <Route path="/" element={user ? (isAdmin ? <Navigate to="/dashboard" /> : <Navigate to="/espace-client" />) : <LandingPage />} />
        <Route path="/login" element={user ? (isAdmin ? <Navigate to="/dashboard" /> : <Navigate to="/espace-client" />) : <LoginPage onLogin={setUser} />} />
        <Route path="/inscription" element={user ? <Navigate to="/" /> : <RegisterPage />} />
        <Route path="/mot-de-passe-oublie" element={user ? <Navigate to="/" /> : <ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/cgu" element={<CGUPage />} />
        <Route path="/cgv" element={<CGVPage />} />
        <Route path="/confidentialite" element={<ConfidentialitePage />} />

        {/* Client Portal */}
        {isClient && (
          <>
            <Route path="/espace-client" element={<ClientPortalPage user={user} onLogout={handleLogout} />} />
            <Route path="/espace-client/rapport/:reportId" element={<ClientReportViewer user={user} onLogout={handleLogout} />} />
            <Route path="/espace-client/rapport-mensuel" element={<MonthlyReportPage user={user} />} />
            <Route path="/espace-client/rapport-mensuel/:period" element={<MonthlyReportPage user={user} />} />
            <Route path="/espace-client/rapport-mensuel/:period/:section" element={<MonthlyReportPage user={user} />} />
          </>
        )}

        {/* Admin */}
        {isAdmin && (
          <>
            <Route path="/dashboard" element={<AdminLayout user={user} onLogout={handleLogout}><DashboardPage /></AdminLayout>} />
            <Route path="/clients" element={<AdminLayout user={user} onLogout={handleLogout}><ClientsPage /></AdminLayout>} />
            <Route path="/data" element={<AdminLayout user={user} onLogout={handleLogout}><DataEntryPage /></AdminLayout>} />
            <Route path="/reports" element={<AdminLayout user={user} onLogout={handleLogout}><ReportsPage /></AdminLayout>} />
            <Route path="/reports/:reportId" element={<AdminLayout user={user} onLogout={handleLogout}><ReportViewerPage /></AdminLayout>} />
            <Route path="/sectors" element={<AdminLayout user={user} onLogout={handleLogout}><SectorsPage /></AdminLayout>} />
            <Route path="/import" element={<AdminLayout user={user} onLogout={handleLogout}><ImportDataPage /></AdminLayout>} />
            <Route path="/pending" element={<AdminLayout user={user} onLogout={handleLogout}><PendingClientsPage /></AdminLayout>} />
            <Route path="/analytics" element={<AdminLayout user={user} onLogout={handleLogout}><AnalyticsPage /></AdminLayout>} />
            <Route path="/cartographie" element={<AdminLayout user={user} onLogout={handleLogout}><CartographyPage /></AdminLayout>} />
            <Route path="/kpis" element={<AdminLayout user={user} onLogout={handleLogout}><KPIsPage /></AdminLayout>} />
            <Route path="/admin/campagnes" element={<AdminLayout user={user} onLogout={handleLogout}><CampaignsPage /></AdminLayout>} />
            <Route path="/admin/cabinet/prompts" element={<AdminLayout user={user} onLogout={handleLogout}><ReportPromptsAdminPage /></AdminLayout>} />
            <Route path="/admin/cabinet/revue" element={<AdminLayout user={user} onLogout={handleLogout}><ReportReviewPage /></AdminLayout>} />
            <Route path="/admin/veille-proactive/health" element={<AdminLayout user={user} onLogout={handleLogout}><VeilleProactiveHealthPage /></AdminLayout>} />
          </>
        )}

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
